import { useEffect, useState } from "react";
import {
  CalendarCheck2,
  Mail,
  Phone,
  ShieldOff,
  ShieldCheck,
} from "lucide-react";

const AdminUserManager = () => {
  const [users, setUsers] = useState([]);
  const [alert, setAlert] = useState({ message: "", type: "", visible: false });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const showAlert = (message, type = "success") => {
    setAlert({ message, type, visible: true });
    setTimeout(() => setAlert({ message: "", type: "", visible: false }), 3000);
  };

  useEffect(() => {
    const fetchUsersAndOrders = async () => {
      try {
        const [userRes, orderRes] = await Promise.all([
          fetch(`${BASE_URL}/api/admin/users`, { credentials: "include" }),
          fetch(`${BASE_URL}/api/admin/orders`, { credentials: "include" }),
        ]);

        if (!userRes.ok || !orderRes.ok) throw new Error("Fetch failed");

        const usersData = await userRes.json();
        const ordersData = await orderRes.json();

        const enrichedUsers = usersData.map((user) => {
          const userOrders = ordersData.filter((order) => {
            const orderUserId =
              typeof order.user === "object" && order.user !== null
                ? order.user._id
                : order.user;
            return orderUserId?.toString() === user._id?.toString();
          });

          userOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          const lastOrderDate = userOrders.length
            ? new Date(userOrders[0].createdAt).toLocaleDateString()
            : "N/A";

          return {
            ...user,
            totalOrders: userOrders.length,
            lastOrderDate,
          };
        });

        setUsers(enrichedUsers);
      } catch (err) {
        console.error("Failed to fetch users or orders", err);
        showAlert("Failed to load users", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchUsersAndOrders();
  }, [BASE_URL]);


  const toggleActivation = async (userId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/users/${userId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("Activation toggle failed");

      const { updatedUser } = await res.json();
      setUsers((prev) =>
        prev.map((user) =>
          user._id === userId
            ? {
                ...updatedUser,
                totalOrders: user.totalOrders,
                lastOrderDate: user.lastOrderDate,
              }
            : user
        )
      );
      showAlert(
        `User ${updatedUser.email} has been ${updatedUser.status === "active" ? "reactivated" : "deactivated"}.`
      );
    } catch (error) {
      console.error(error);
      showAlert("Failed to toggle user activation", "error");
    }
  };

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);

  return (
    <section className="max-w-6xl mx-auto px-4 py-10">
      {/* ✅ Banner Notification */}
      {alert.visible && (
        <div
          className={`mb-4 px-4 py-3 rounded text-sm ${
            alert.type === "success"
              ? "bg-green-100 text-green-800"
              : alert.type === "error"
              ? "bg-red-100 text-red-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {alert.message}
        </div>
      )}

      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        User Manager
      </h1>

      {loading ? (
        <p className="text-gray-500">Loading users...</p>
      ) : (
        <div className="overflow-x-auto bg-white shadow rounded-lg border border-gray-200">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Total Orders</th>
                <th className="px-4 py-3">Last Order</th>
                <th className="px-4 py-3">Verified</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user, idx) => (
                <tr
                  key={user._id}
                  className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-4 py-3 font-medium">{user.name}</td>
                  <td className="px-4 py-3 text-blue-600 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> {user.email}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {user.phone ? (
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        {user.phone}
                      </span>
                    ) : (
                      <span className="italic text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{user.totalOrders}</td>
                  <td className="px-4 py-3 flex items-center gap-2 text-gray-600">
                    <CalendarCheck2 className="w-4 h-4" /> {user.lastOrderDate}
                  </td>
                  <td className="px-4 py-3">
                    {user.isVerified ? (
                      <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                        <ShieldCheck className="w-4 h-4" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                        <ShieldOff className="w-4 h-4" /> Unverified
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                    {/* Toggle Active/Inactive */}
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to ${
                              user.status === "active" ? "deactivate" : "activate"
                            } ${user.email}?`
                          )
                        ) {
                          toggleActivation(user._id);
                        }
                      }}
                      className={`text-xs px-3 py-1 rounded font-medium transition-colors duration-200 ${
                        user.status === "active"
                          ? "bg-red-100 text-red-700 hover:bg-red-200"
                          : "bg-green-100 text-green-700 hover:bg-green-200"
                      }`}
                    >
                      {user.status === "active" ? (
                        <span className="flex items-center gap-1">
                          <ShieldOff className="w-4 h-4" />
                          Deactivate
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <ShieldCheck className="w-4 h-4" />
                          Activate
                        </span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-center mt-4 gap-2">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>

        {Array.from({ length: Math.ceil(users.length / usersPerPage) }, (_, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            className={`px-3 py-1 border rounded ${
              currentPage === i + 1 ? "bg-gray-200 font-bold" : ""
            }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          onClick={() =>
            setCurrentPage((prev) =>
              prev < Math.ceil(users.length / usersPerPage) ? prev + 1 : prev
            )
          }
          disabled={currentPage === Math.ceil(users.length / usersPerPage)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </section>
  );
};

export default AdminUserManager;
